import posthog from 'posthog-js';
import { useEffect } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { useSetupProgress } from '@/hooks/useSetupProgress';
import { isInternalEmail } from './internalEmail';

/** Start/stop the global recording idempotently. */
export function applyRecording(shouldRecord: boolean): void {
    if (shouldRecord) {
        if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording();
    } else if (posthog.sessionRecordingStarted()) {
        posthog.stopSessionRecording();
    }
}

/**
 * Records an external prospect through the in-backoffice onboarding funnel and
 * stops the moment the widget goes live. `isLive` is false only while
 * `onboarding.active` is set and `onboarding.installed` is not — i.e. from the
 * domain claim until the install step marks Rose live. Established/live domains
 * read as live and are never recorded, and internal testers never record either.
 *
 * Mounted inside AppLayout because it needs the workspace config. The pre-claim
 * wizard runs outside AppLayout and is covered in BackofficeAuthContext.
 */
export function OnboardingSessionReplay(): null {
    const { user } = useAuth();
    const { backofficeUser } = useBackofficeAuth();
    const { isLive, isLiveLoading } = useSetupProgress();

    useEffect(() => {
        // Wait for the config-backed live decision so we neither record an
        // established user during load nor stop a prospect prematurely.
        if (isLiveLoading) return;
        const external = !isInternalEmail(user?.email ?? backofficeUser?.email);
        applyRecording(external && !isLive);
    }, [user, backofficeUser, isLive, isLiveLoading]);

    return null;
}
